import { useQuery } from '@tanstack/react-query';
import { Car, MessageSquarePlus, Star, X } from 'lucide-react';
import { instructorApi } from '../api/instructor';
import { useUiStore } from '../store/ui';
import { fmtDate } from '../lib/dates';
import type { StudentLesson } from '../types';

const LEVEL_LABEL: Record<string, string> = {
  acquis: 'Acquis',
  en_cours: 'En cours',
  a_revoir: 'À revoir',
};

function LessonRow({
  lesson,
  onBilan,
}: {
  lesson: StudentLesson;
  onBilan: () => void;
}) {
  const b = lesson.bilan;
  return (
    <div className="rounded-2xl border border-slate-200 p-4 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center flex-shrink-0">
            <Car className="text-indigo-600" size={16} />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold text-slate-900">
              {fmtDate(lesson.startsAt)}
            </div>
            <div className="text-xs text-slate-500">
              {lesson.lessonType ?? 'Leçon'} · {lesson.durationHours} h
            </div>
          </div>
        </div>
        <button
          onClick={onBilan}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 text-xs font-medium hover:bg-slate-50 transition-colors flex-shrink-0"
        >
          <MessageSquarePlus size={14} className="text-indigo-600" />
          {b ? 'Compléter le bilan' : 'Ajouter un bilan'}
        </button>
      </div>

      {b && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <div className="flex items-center gap-2 mb-1">
            {b.readyForExam && (
              <span className="px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200 text-[11px] font-semibold">
                Prêt examen
              </span>
            )}
            {b.rating != null && (
              <span className="flex items-center gap-0.5 text-amber-500 text-xs">
                <Star size={13} className="fill-amber-400 text-amber-400" />
                {b.rating}/5
              </span>
            )}
          </div>
          <p className="text-sm text-slate-600 whitespace-pre-line">
            {b.summary}
          </p>
          {b.nextObjective&&<p className="mt-2 text-sm text-indigo-700"><strong>Prochain cours :</strong> {b.nextObjective}</p>}
          {b.examReason&&<p className="mt-2 text-sm">Avis examen : {b.examReason}</p>}
          {b.competencies && b.competencies.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {b.competencies.map((c, i) => (
                <span
                  key={`${c.label}-${i}`}
                  className="px-2.5 py-1 rounded-full border border-slate-200 bg-slate-50 text-[11px] font-medium text-slate-600"
                >
                  {c.label} · {LEVEL_LABEL[c.level] ?? c.level}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function LessonsModal() {
  const target = useUiStore((s) => s.lessonsStudent);
  const close = useUiStore((s) => s.closeLessons);
  const openBilan = useUiStore((s) => s.openBilan);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['student-lessons', target?.id],
    queryFn: () => instructorApi.getStudentLessons(target!.id),
    enabled: !!target,
  });

  if (!target) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl border border-slate-200 max-h-[90vh] overflow-y-auto animate-fade-in">
        <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white">
          <div>
            <h2 className="text-slate-900 text-lg font-semibold">
              Historique des leçons
            </h2>
            <p className="text-xs text-slate-500">{target.name}</p>
          </div>
          <button
            onClick={close}
            className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} className="text-slate-500" />
          </button>
        </div>
        <div className="px-6 py-5 space-y-3">
          {isLoading && <p className="text-slate-500">Chargement…</p>}
          {isError && (
            <p className="text-red-600">
              Impossible de charger les leçons.
            </p>
          )}
          {data && data.length === 0 && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-slate-500 text-sm">
              Aucune leçon passée pour cet élève.
            </div>
          )}
          {data?.map((lesson) => (
            <LessonRow
              key={lesson.id}
              lesson={lesson}
              onBilan={() =>
                openBilan({
                  candidateId: target.id,
                  candidateName: target.name,
                  lessonId: lesson.id,
                })
              }
            />
          ))}
        </div>
      </div>
    </div>
  );
}
